import {GridColumnType, GridFlexProps} from '../../types';
import classNames from 'classnames';
import styles from '../../grid.module.scss';
import React, {HTMLAttributes, useEffect} from 'react';
import moment from 'moment';
import {SVGS} from '../../../../../assets/images';
import { ClickAwayListener } from '@mui/material';

const formatDateCell = (value: any): string => {
    return moment(value as Date).format('L')
}

const FormatCell = (props: { value: any, hover: boolean, editable?: boolean, type?: GridColumnType, placeholder?: string }): React.ReactElement => {
    const { value, hover, type, editable } = props;
    if (React.isValidElement(value)) {
        return value;
    }

    if (!type) {
        return (<>{value}</>)
    }

    switch (type) {
        case 'date':
            return (<>{formatDateCell(value)}</>)
        case 'singleSelect':
            return (<span className={classNames({
                [styles.dropdownCell]: true,
                [styles.editable]: editable
            })}><span>{value}</span><SVGS.DropdownIcon /></span>)
        case 'text':
            return (<span className={styles.cellText}>
                {value}
                <span className={classNames({
                    [styles.placeholder]: props.placeholder !== undefined,
                    [styles.active]: !value,
                    [styles.hover]: hover
                })}>{props.placeholder}</span>
            </span>)
        default:
            return (<span className={styles.value}>{value}</span>);
    }
}

interface Props {
    flex: boolean | GridFlexProps;
    muted: boolean;
    editable: boolean;
    type: GridColumnType;
    children: any;
    active: boolean;
    onOutsideClick: () => void;
    cellProps: React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    placeholder?: string;
    setBackground?: boolean;
    position: 'start' | 'end';
    alignment: 'start' | 'end' | 'center';
    disabled?: boolean;
    className?: string;
}

const Cell = (props: Props) => {
    const { active, children, onOutsideClick, cellProps, position, alignment } = props;
    const [hover, setHover] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!props.editable || props.disabled) {
            return
        }

        setHover(true)
        cellProps.onMouseEnter?.(event)
    };

    const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!props.editable || props.disabled) {
            return
        }

        setHover(false)
        cellProps.onMouseLeave?.(event)
    };

    const handleClickAway = () => {
        const root = ref.current;
        const activeElement = document.activeElement;

        if (root && activeElement instanceof HTMLElement && root.contains(activeElement)) {
            activeElement.blur();
        }

        onOutsideClick();
    };

    return(<ClickAwayListener onClickAway={handleClickAway}>
        <div className={classNames({
            [styles.cell]: true,
            [styles.muted]: props.muted || props.disabled,
            [styles.editable]: props.editable && !props.disabled,
            [styles.text]: props.type == 'string' || props.type === 'text' || props.type === 'date',
            [styles.date]: props.type === 'date',
            [styles.active]: props.active,
            [styles.setBackground]: props.setBackground,
            [styles.end]: position === 'end',
            [styles.alignCenter]: alignment === 'center',
            [styles.alignBottom]: alignment === 'end'
        }, props.className)}
        ref={ref}
        {...cellProps}
        onMouseEnter={(event) => handleMouseEnter(event)}
        onMouseLeave={(event) => handleMouseLeave(event)}>
            {
                active ? children :
                    <FormatCell value={children} type={props.type} editable={props.editable}
                        hover={hover} placeholder={props.placeholder} />
            }
        </div>
    </ClickAwayListener>)
}

export default Cell;
